import { Switch, Route, useLocation } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/Dashboard";
import Journal from "@/pages/Journal";
import MediaLibrary from "@/pages/MediaLibrary";
import FamilyCircle from "@/pages/FamilyCircle";
import Recruiting from "@/pages/Recruiting";
import StatsPage from "@/pages/StatsPage";
import NewLanding from "@/pages/NewLanding";
import Login from "@/pages/Login";
import WelcomeSetup from "@/pages/WelcomeSetup";
import ComingSoon from "@/pages/ComingSoon";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ContactUs from "@/pages/ContactUs";
import HelpCenter from "@/pages/HelpCenter";
import VaultPage from "@/pages/VaultPage";
import Subscription from "@/pages/Subscription";
import Profile from "@/pages/Profile";
import Milestones from "@/pages/Milestones";
import AdminDashboard from "@/pages/AdminDashboard";
import SharePage from "@/components/SharePage";
import LoopView from "@/pages/LoopView";
import ResetPassword from "@/pages/ResetPassword";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/hooks/useAuth";
import { ActiveAthleteProvider } from "@/hooks/useActiveAthlete";
import { useEffect } from "react";
import { cn } from "@/lib/utils";

// App.tsx
function ProtectedRoute({ component: Component, ...rest }: { component: React.ComponentType, [key: string]: any }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, navigate] = useLocation();

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      // Allow the public landing page unauthenticated
      if (location === "/") return;

      // 🔁 Send to login *with* return destination
      navigate(`/login?redirect=${encodeURIComponent(location)}`);
      return;
    }
  }, [isAuthenticated, isLoading, location, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // If we're on "/" let it render as public; other pages will have navigated above
    if (location === "/") return <Component {...rest} />;
    return null;
  }

  return <Component {...rest} />;
}

function App() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();
  const isLoopView = location.startsWith("/loop/");
  const showHeader = isAuthenticated && location !== "/welcome-setup" && !isLoopView;

  return (
    <TooltipProvider>
      <ActiveAthleteProvider>
      <div className={cn("flex flex-col min-h-screen bg-gradient-to-br from-[#060d18] via-[#0D1B2A] to-[#1B263B]")}>
        {showHeader && <Header />}
        
        <main className="flex-grow">
          <Switch>
            {/* Public routes that don't require authentication */}
            <Route path="/" component={NewLanding} />
            <Route path="/login" component={Login} />
            <Route path="/reset-password" component={ResetPassword} />
            <Route path="/coming-soon" component={ComingSoon} />
            <Route path="/welcome-setup">
              {() => <ProtectedRoute component={WelcomeSetup} />}
            </Route>
            <Route path="/terms" component={TermsOfService} />
            <Route path="/privacy" component={PrivacyPolicy} />
            <Route path="/contact" component={ContactUs} />
            <Route path="/help" component={HelpCenter} />
            <Route path="/share/:shareCode" component={SharePage} />
            <Route path="/loop/:token" component={LoopView} />
            
            {/* Demo route for presentation purposes */}
            <Route path="/demo">
              {() => <Dashboard />}
            </Route>
            
            {/* Protected routes that require authentication */}
            <Route path="/dashboard">
              {() => <ProtectedRoute component={Dashboard} />}
            </Route>
            <Route path="/journal">
              {() => <ProtectedRoute component={Journal} />}
            </Route>
            <Route path="/media">
              {() => <ProtectedRoute component={MediaLibrary} />}
            </Route>
            <Route path="/family">
              {() => <ProtectedRoute component={FamilyCircle} />}
            </Route>
            <Route path="/vault">
              {() => <ProtectedRoute component={VaultPage} />}
            </Route>
            <Route path="/recruiting">
              {() => <ProtectedRoute component={Recruiting} />}
            </Route>
            <Route path="/stats">
              {() => <ProtectedRoute component={StatsPage} />}
            </Route>
            <Route path="/subscription">
              {() => <ProtectedRoute component={Subscription} />}
            </Route>
            <Route path="/profile">
              {() => <ProtectedRoute component={Profile} />}
            </Route>
            <Route path="/milestones">
              {() => <ProtectedRoute component={Milestones} />}
            </Route>
            <Route path="/admin">
              {() => <ProtectedRoute component={AdminDashboard} />}
            </Route>

            {/* Fallback for unknown routes */}
            <Route component={NotFound} />
          </Switch>
        </main>
        
        {!isLoopView && <Footer />}
      </div>
      <Toaster />
      </ActiveAthleteProvider>
    </TooltipProvider>
  );
}

export default App;
